import { Fragment, useEffect, useMemo, useState } from 'react'
import {
  GetServerSideProps,
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
} from 'next'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useQuery } from '@tanstack/react-query'
import {
  CertificateProgramEnrollmentStatus,
  CertificateProgramsService,
  CurriculumStatus,
  getCertificateEnrollmentAccessState,
  hasExpiredCertificateProgramAccess,
  type CertificateProgram,
  type CertificateProgramProduct,
  type CertificateSubscriptionOffer,
} from '@mc/api'
import { useCertificateEnrollments } from '@mc/api-hooks'
import { ErrorPage } from '@mc/error-page'
import { reportError } from '@mc/next-newrelic'
import { getUserAttributes } from '@mc/next-user-attributes'
import { paths } from '@mc/paths'
import {
  getCertificateSubscriptionOffer,
  getMyCertificateSubscription,
} from 'libs/server/api/certificateSubscriptions'
import {
  getCertificateProduct,
  getCertificateProductWithDiscount,
  getCertificateProgram,
  getCertificateUuid,
} from 'libs/server/api/certificates'
import { getPageData, PageDataResult } from 'libs/server/page-data'
import { getSubscriptionDiscountCandidates } from 'pages/certificates/annualpass/data/subscriptionData'
import { CohortLandingPage } from 'pages/certificates/cohorts/CohortLandingPage'
import { isCohortSlug } from 'pages/certificates/cohorts/getCohort'
import {
  getCohortPageProps,
  isCohortPageProps,
  type CohortPageProps,
} from 'pages/certificates/cohorts/getCohortPageProps'
import { BaseProviders } from 'pages/certificates/common/BaseProviders'
import {
  PreEnrolledNavigation,
  TrackingRow,
  TrackingRowProvider,
} from 'pages/certificates/components'
import { KeyBenefitsSection } from 'pages/certificates/components/MarketingCertPage'
import { PromoBanner } from 'pages/certificates/components/PromoBanner/PromoBanner'
import { MCCERTS_1928_SECONDARY_CTA_REMOVAL_FLAG } from 'pages/certificates/experiments/mccerts-1928-secondary-cta-removal'
import {
  getPaintedDoorV2Experiment,
  isPaintedDoorSlug,
  PAINTED_DOOR_PRODUCTS,
  PAINTED_DOOR_PROGRAMS,
} from 'pages/certificates/experiments/mccerts-2138-painted-door-v2'
import { isSubsEntryPointSelectorVariantSsr } from 'pages/certificates/experiments/mccerts-2548_v1-subscriptions-entry-point'
import {
  MCCERTS_2882_SUBSCRIPTION_PRICE_TEST_FLAG,
  getPriceTest,
  usePriceTestFallbackTelemetry,
  type SubscriptionPriceTestBucket,
} from 'pages/certificates/experiments/mccerts-2882-subscription-price-test'
import {
  CMBannerCtaSubsOnlyExp,
  CMHeroSectionSubsOnlyExp,
  CMNavSignUpCtaSubsOnlyExp,
  CMPromoModalSubsOnlyExp,
  isSubsOnlyClpExpSsr,
  MCCERTS_2972_SUBS_ONLY_CLP_FLAG,
  useIsSubsOnlyClpExpActive,
} from 'pages/certificates/experiments/mccerts-2972-subs-only-clp-v1'
import {
  MCCERTS_3255_MC_PRO_IN_CERTS_FLAG,
  useIsMcProInCertsExpActive,
} from 'pages/certificates/experiments/mccerts-3255-mc-pro-in-certs-v1'
import {
  getMccerts3381Variables,
  MCCERTS_3381_UBER_CLP_HERO_COPY_FLAG,
} from 'pages/certificates/experiments/mccerts-3381_v1_uber_clp_hero_copy'
import { isPromoCopyAllowed, resolvePromoCopy } from 'pages/certificates/promo'
import { collectDiscountCandidates } from 'pages/certificates/promo/collectDiscountCandidates'
import {
  getPromoVariables,
  resolveDiscount,
  type PromoVariables,
  type SubscriptionPromoVariables,
} from 'pages/certificates/promo/server'
import { applyMccerts2664Overrides } from 'pages/certificates/utils/applyMccerts2664Overrides'
import {
  EXPERIMENT_FLAG as MCCERTS_2664_CERT_PAGE_COPY_FLAG,
  getMccerts2664Variables,
  type Mccerts2664Data,
} from 'pages/certificates/utils/getMccerts2664Variables'
import {
  getWhoIsThisForVariables,
  type WhoIsThisForData,
} from 'pages/certificates/utils/getWhoIsThisForVariables'
import {
  CERTIFICATES,
  CertificateSlugProvider,
  type CertificateInfo,
  getLatestCertificates,
  IsPurchaseCtaEnabledProvider,
  LatestCertificateInfo,
  ModalLockProvider,
} from '../common'
import { Layout } from '../common/Layout'
import { Meta } from '../common/Meta'
import { TrackClick, TrackingAttributes, TrackProductViewed } from '../tracking'
import {
  TrackExperimentViewed,
  TrackGoldStandardPageViewed,
} from '../tracking/certificatesTracking'
import { getInstructorOverrides } from '../utils/getInstructorOverrides'

const BelowTheFold = dynamic(() => import('./BelowTheFold'), {
  ssr: false,
})

const VanityModal = dynamic(
  () => import('../components').then((mod) => mod.VanityModal),
  {
    ssr: false,
  },
)

const PAGE_SOURCE = 'Certificates Overview Landing'

type CertificatePageProps = {
  certSlug: string
  certificates: CertificateInfo[]
  latestCertificates: LatestCertificateInfo[]
  program: CertificateProgram
  product?: CertificateProgramProduct
  products?: CertificateProgramProduct[]
  userIsLoggedIn: boolean
  promoVariables?: PromoVariables | null
  // mccerts-2548_v1: subscription promo payload for the variant_1 sticky footer
  subscriptionPromoVariables?: SubscriptionPromoVariables | null
  vanityDiscountSlugs?: string[]
  whoIsThisFor?: WhoIsThisForData | null
  mccerts2664?: Mccerts2664Data | null
  mccerts3381?: Mccerts2664Data | null
  /**
   * Whether promo copy is allowed to render given the best-coupon winner.
   * `true` unless candidates were sent and the per-cert promo lost.
   */
  promoCopyAllowed: boolean
  aapUser: boolean
  isPaintedDoor?: boolean
  // FCC offer for the CM hero, resolved server-side because the hero is above
  // the fold. Null unless the viewer is in a mccerts-2548_v1 selector variant
  // or any mccerts-2972 arm (or when the offer is unavailable).
  subscriptionOffer?: CertificateSubscriptionOffer | null
  // whether the viewer holds an FCC subscription (from /mine).
  // Subscribers already own the catalog, so purchase/promo/conversion modules
  // are suppressed on this pre-enroll page.
  isSubscribed?: boolean
  subscriptionPriceTest?: {
    planSlug: string | null
    bucket: SubscriptionPriceTestBucket | null
  } | null
  isSubsEntryPointSelectorVariant?: boolean
}

export const CertificateImpl = ({
  certSlug,
  certificates,
  product,
  latestCertificates,
  userIsLoggedIn,
  promoVariables: sourcePromoVariables,
  subscriptionPromoVariables: sourceSubscriptionPromoVariables,
  vanityDiscountSlugs,
  whoIsThisFor,
  mccerts2664,
  mccerts3381,
  program,
  aapUser,
  isPaintedDoor,
  subscriptionOffer, // mccerts-2548_v1, mccerts-2972
  isSubscribed = false,
  subscriptionPriceTest,
  isSubsEntryPointSelectorVariant,
  // Default true so any call site that omits it (e.g. test harnesses) keeps
  // pre-best-coupon behavior rather than silently suppressing promo copy.
  promoCopyAllowed = true,
}: CertificatePageProps) => {
  const router = useRouter()

  usePriceTestFallbackTelemetry(
    subscriptionPriceTest?.planSlug,
    subscriptionOffer?.subscriptionPlan?.slug,
  )

  const comingSoon =
    (program.curriculum?.details.status as CurriculumStatus) === 'coming_soon'
  const available =
    (program.curriculum?.details.status as CurriculumStatus) === 'available'

  // mccerts-2664_v1: when the server resolved variant copy/imagery for this
  // cert, render a program with those overrides applied; otherwise (control /
  // flag off) render the untouched program. The original `program` is still used
  // for <Meta>, <Head><title>, tracking, and the nav.
  const displayProgram = useMemo(() => {
    const withMccerts2664 = mccerts2664
      ? applyMccerts2664Overrides(program, mccerts2664)
      : program
    return mccerts3381
      ? applyMccerts2664Overrides(withMccerts2664, mccerts3381)
      : withMccerts2664
  }, [program, mccerts2664, mccerts3381])

  // Performance note:
  // This marketing page will receive considerable traffic, and ads will point to it.
  // For this reason, user-related information requests should never block page load for logged-out users, who make up the majority of the traffic, that's why this request is enabled only for logged-in users.
  // Keyed by program, not a boolean: the carousel moves between landing pages
  // client-side without remounting this component.
  const [checkedProgramUuid, setCheckedProgramUuid] = useState<string>()
  const isCheckingEnrollment =
    !!userIsLoggedIn && !isPaintedDoor && checkedProgramUuid !== program.uuid
  const {
    data: interactions,
    isSuccess: interactionsLoaded,
    isFetchedAfterMount: interactionsFetchedAfterMount,
  } = useQuery(
    ['interactions', program.uuid],
    () =>
      CertificateProgramsService.getCertificateProgramInteractions({
        certificateProgramUuid: program.uuid,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !!userIsLoggedIn && !isPaintedDoor,
    },
  )

  // The interactions payload only knows about live enrollments, so an expired
  // learner is found through the enrollments list instead.
  const {
    data: enrollments,
    isFetchedAfterMount: enrollmentsFetchedAfterMount,
  } = useCertificateEnrollments(
    {
      active: true,
      pending: true,
      expired: true,
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!userIsLoggedIn && !isPaintedDoor,
      onError: (error) =>
        reportError(error, {
          source: 'certificate-landing-enrollments',
          certificateProgramUuid: program.uuid,
        }),
    },
  )

  // Checkout soft-navigates back here, so both queries can hold pre-purchase
  // data from the cache. Decide only from data fetched during this mount.
  useEffect(() => {
    if (
      !isCheckingEnrollment ||
      !interactionsLoaded ||
      !interactionsFetchedAfterMount ||
      !enrollmentsFetchedAfterMount
    ) {
      return
    }
    // A consent-blocked learner holds a live entitlement, so they belong in the
    // product behind the consent gate, not on the sales page.
    const accessState = getCertificateEnrollmentAccessState(
      interactions?.enrollment,
    )
    const hasActiveAccess =
      accessState === 'active' || accessState === 'consent_required'
    if (
      hasActiveAccess ||
      hasExpiredCertificateProgramAccess(enrollments, program.uuid)
    ) {
      router.replace(paths.certificateOverview(certSlug))
      return
    }
    setCheckedProgramUuid(program.uuid)
  }, [
    isCheckingEnrollment,
    interactionsLoaded,
    interactionsFetchedAfterMount,
    enrollmentsFetchedAfterMount,
    interactions,
    enrollments,
    program.uuid,
    certSlug,
    router,
  ])

  // Do not show promo to users who have already purchased a pre-enroll, and —
  // with best-coupon — only when the per-cert promo actually won (promoCopyAllowed).
  const showPromo =
    interactions?.enrollment?.status !==
      CertificateProgramEnrollmentStatus.PENDING && program.signupEnabled
  // Surface/state selection lives in promo/ (the cert surface, coming-soon or
  // launched); the page only decides whether a promo may show at all.
  // isPreEnroll comes straight from the backend's CertificateProgram#pre_enroll?
  // (single source of truth — see CertificateCard.tsx's tile badge for the same
  // pattern). `comingSoon || isPreEnroll` still layers any plain coming-soon
  // override underneath, same as CertificateCard.tsx's isComingSoon || isPreEnroll.
  const isPreEnroll = !!program.isPreenroll
  const promoVariables =
    showPromo && promoCopyAllowed
      ? resolvePromoCopy(sourcePromoVariables, {
          surface: 'cert',
          comingSoon: comingSoon || isPreEnroll,
          preEnroll: isPreEnroll,
        })
      : undefined
  // mccerts-2548_v1: subscription promo payload for the variant_1 sticky footer,
  // layered with any productSurface.subscription override.
  const subscriptionPromoVariables = resolvePromoCopy(
    sourceSubscriptionPromoVariables,
    { surface: 'subscription' },
  )
  const banner = promoVariables?.banner
  const bannerCta = banner ? (
    <TrackClick event='purchaseClick'>
      <CMBannerCtaSubsOnlyExp
        kind={banner.buttonKind}
        arrowTheme={banner.buttonArrowTheme}
        program={displayProgram}
        engagement={interactions?.engagement}
        isPaintedDoor={isPaintedDoor}
      />
    </TrackClick>
  ) : null

  const navigationActions = interactions?.enrollment?.isEnrolled
    ? interactions?.engagement?.navigation?.actions
    : program.defaultEngagement?.navigation?.actions

  const isSubsOnlyClpExpActive = useIsSubsOnlyClpExpActive({
    program: displayProgram,
    engagement: interactions?.engagement,
    isPaintedDoor,
  })
  const isMcProInCertsExpActive = useIsMcProInCertsExpActive({
    program: displayProgram,
    engagement: interactions?.engagement,
    isPaintedDoor,
  })

  if (isCheckingEnrollment) return null

  return (
    <Fragment key={program.uuid}>
      <Meta
        title={product?.name}
        description={product?.description}
        image={program.images?.hero1x1?.url}
        imageAlt={program.images?.hero1x1?.altText}
      />
      <TrackExperimentViewed name={MCCERTS_1928_SECONDARY_CTA_REMOVAL_FLAG} />
      <TrackExperimentViewed
        name={MCCERTS_2882_SUBSCRIPTION_PRICE_TEST_FLAG}
        enabled={
          !!subscriptionPriceTest?.bucket && !!isSubsEntryPointSelectorVariant
        }
      />
      <TrackGoldStandardPageViewed
        name={
          isPaintedDoor
            ? 'Certificate Topic Landing'
            : 'Certificate Overview Landing'
        }
        product={isPaintedDoor ? 'Certificate Topics' : 'certificates'}
        product_name={product?.name}
      />
      <TrackingAttributes
        product={isPaintedDoor ? 'Certificate Topics' : 'certificates'}
        product_name={product?.name}
        page_source={
          isPaintedDoor
            ? 'Certificate Topic Landing'
            : 'Certificate Overview Landing'
        }
        certificate_id={program.uuid}
      >
        {product && <TrackProductViewed product={product} />}
        {/* mccerts-2664: fire exposure INSIDE the cert-overview TrackingAttributes
            so the Experiment Viewed event carries the correct product /
            product_name / page_source / certificate_id (matches how other cert
            experiments scope their EV, e.g. incentivized-completion). Gated on
            `mccerts2664` so it fires only on configured certs, both arms. The
            server-side GrowthBook eval fires no exposure (no trackingCallback),
            so exposure is fired client-side here. */}
        <TrackExperimentViewed
          name={MCCERTS_2664_CERT_PAGE_COPY_FLAG}
          enabled={!!mccerts2664}
        />
        <TrackExperimentViewed
          name={MCCERTS_2972_SUBS_ONLY_CLP_FLAG}
          enabled={isSubsOnlyClpExpActive}
          certificate_id={program.uuid}
          product_name={product?.name}
        />
        <TrackExperimentViewed
          name={MCCERTS_3255_MC_PRO_IN_CERTS_FLAG}
          enabled={isMcProInCertsExpActive}
          certificate_id={program.uuid}
          product_name={product?.name}
        />
        <TrackExperimentViewed
          name={MCCERTS_3381_UBER_CLP_HERO_COPY_FLAG}
          enabled={!!mccerts3381}
          certificate_id={program.uuid}
          product_name={product?.name}
        />
        <Head>
          <title>{program.title}</title>
        </Head>
        <CertificateSlugProvider value={{ certSlug }}>
          <IsPurchaseCtaEnabledProvider
            promoVariables={promoVariables}
            engagement={interactions?.engagement ?? program.defaultEngagement}
          >
            <ModalLockProvider>
              <Layout backgroundColor='var(--c-beige-600)'>
                <TrackingRowProvider event='certificateRowViewed'>
                  <TrackingAttributes
                    location='nav bar'
                    certificate_id={program.uuid}
                  >
                    <PreEnrolledNavigation
                      topics={certificates}
                      latestCertificates={latestCertificates}
                      currentCertificateTitle={program.title}
                      currentCertificateImg={{
                        url: program.images?.hero1x1?.url,
                        altText: program.images?.hero1x1?.altText,
                      }}
                      currentCertificateSignupEnabled={program.signupEnabled}
                      navigationActions={navigationActions}
                      redirectTo={paths.certificates}
                      aapUser={aapUser}
                      signUpButton={
                        <CMNavSignUpCtaSubsOnlyExp
                          program={displayProgram}
                          engagement={interactions?.engagement}
                          isPaintedDoor={isPaintedDoor}
                        />
                      }
                    />
                  </TrackingAttributes>
                  {banner && (
                    <TrackingAttributes location='promo banner'>
                      <PromoBanner
                        banner={banner}
                        cta={bannerCta}
                        promo={promoVariables?.promo}
                      />
                    </TrackingAttributes>
                  )}
                  <CMPromoModalSubsOnlyExp
                    program={displayProgram}
                    engagement={interactions?.engagement}
                    isPaintedDoor={!!isPaintedDoor}
                    promoVariables={promoVariables}
                    subscriptionPromoVariables={
                      showPromo ? subscriptionPromoVariables : null
                    }
                    certThumbnail={displayProgram.images?.thumbnail16x9}
                    releaseLabel={program.releaseLabel}
                    isSubscribed={isSubscribed}
                  />
                  <CMHeroSectionSubsOnlyExp
                    program={displayProgram}
                    engagement={interactions?.engagement}
                    enrollment={interactions?.enrollment}
                    certUuid={program.uuid}
                    certSlug={certSlug}
                    comingSoon={comingSoon}
                    available={available}
                    promoVariables={promoVariables}
                    product={product} // mccerts-2548_v1, mccerts-2972
                    subscriptionOffer={subscriptionOffer} // mccerts-2548_v1, mccerts-2972
                    isPaintedDoor={!!isPaintedDoor} // mccerts-2548_v1, mccerts-2972
                    isSubscribed={isSubscribed}
                  />
                  {program.keyBenefits && (
                    <TrackingRow row_name='essentials'>
                      <KeyBenefitsSection keyBenefits={program.keyBenefits} />
                    </TrackingRow>
                  )}
                  <BelowTheFold
                    certSlug={certSlug}
                    promoVariables={promoVariables}
                    subscriptionPromoVariables={subscriptionPromoVariables}
                    vanityDiscountSlugs={vanityDiscountSlugs}
                    program={displayProgram}
                    product={product}
                    enrollments={enrollments}
                    interactions={interactions}
                    isLoggedIn={userIsLoggedIn}
                    isSubscribed={isSubscribed}
                    isPaintedDoor={!!isPaintedDoor}
                    whoIsThisFor={whoIsThisFor}
                    planSlug={subscriptionPriceTest?.planSlug}
                  />
                </TrackingRowProvider>
              </Layout>
              <VanityModal
                productUuid={product?.uuid}
                certUuid={program.uuid}
                certSlug={certSlug}
              />
            </ModalLockProvider>
          </IsPurchaseCtaEnabledProvider>
        </CertificateSlugProvider>
      </TrackingAttributes>
    </Fragment>
  )
}

const Certificate = (
  props: InferGetServerSidePropsType<typeof getServerSideProps>,
) => {
  if (isCohortPageProps(props)) {
    return <CohortLandingPage {...props} />
  }

  if (props.status === 'error') {
    return <ErrorPage />
  }

  return (
    <BaseProviders>
      <CertificateImpl {...props} />
    </BaseProviders>
  )
}

Certificate.pageSettings = {
  rumLabel: PAGE_SOURCE,
  navigation: null,
  footer: null,
}

export const getServerSideProps: GetServerSideProps<
  PageDataResult<CertificatePageProps> | CohortPageProps
> = async (ctx: GetServerSidePropsContext) => {
  const certSlug = ctx.query.slug as string

  if (isCohortSlug(certSlug)) {
    return getCohortPageProps(ctx, certSlug)
  }

  const isPaintedDoorExpEnabled = await getPaintedDoorV2Experiment(ctx)
  const isPaintedDoor = isPaintedDoorExpEnabled && isPaintedDoorSlug(certSlug)

  // Painted door check - only if flag is enabled AND slug matches
  const uuid = isPaintedDoor
    ? PAINTED_DOOR_PROGRAMS[certSlug].uuid
    : await getCertificateUuid(certSlug, ctx)

  if (!uuid) {
    return {
      redirect: {
        destination: paths.certificates,
        permanent: false,
      },
    }
  }

  const userAttributes = getUserAttributes(ctx, { query: ctx.query })

  // These GrowthBook-variable / override reads are independent — fetch them in
  // parallel to avoid stacking serial latency on this high-traffic SSR page.
  const [
    whoIsThisFor,
    mccerts2664,
    instructorOverrides,
    { planSlug: priceTestPlanSlug, bucket: priceTestBucket },
    mccerts3381,
  ] = await Promise.all([
    getWhoIsThisForVariables(ctx, certSlug),
    getMccerts2664Variables(ctx, certSlug),
    getInstructorOverrides(ctx, certSlug),
    getPriceTest(ctx),
    getMccerts3381Variables(ctx, certSlug),
  ])
  const latestCertificates = getLatestCertificates()

  // Get individual certificate product
  const { discountSlugs, promoCoupon, promoVariables } = await resolveDiscount(
    ctx,
    certSlug,
  )

  // mccerts-2548_v1: subscription promo payload for the variant_1 sticky
  // footer and the V1/V2 subscription offer (evaluated locally against the
  // cached GrowthBook datafile, like the subscription page; enterprise
  // short-circuits to null inside)
  const subscriptionPromoVariables = await getPromoVariables(ctx, {
    promoSurface: 'subscription',
  })
  const vanityDiscountSlugs = getSubscriptionDiscountCandidates(
    subscriptionPromoVariables,
    collectDiscountCandidates(ctx, { promoCoupon: null }),
  )

  // Always send discount candidates so the product response includes coupon
  // data (couponDiscountPercentage, netPrice) for all users with an active promo.
  // Painted-door pages return a stub product without hitting the pricing API.
  const hasCandidates = discountSlugs.length > 0
  const sendDiscountSlugs = !isPaintedDoor && hasCandidates

  const [isSubsEntryPointSelectorVariant, isSubsOnlyClpExp] = await Promise.all(
    [
      isSubsEntryPointSelectorVariantSsr(ctx, { certSlug, isPaintedDoor }),
      isSubsOnlyClpExpSsr(ctx, { certSlug, isPaintedDoor }),
    ],
  )

  const getProduct = () => {
    if (isPaintedDoor) return Promise.resolve(PAINTED_DOOR_PRODUCTS[certSlug])

    if (sendDiscountSlugs) {
      return getCertificateProductWithDiscount(uuid, ctx, discountSlugs)
    }

    return getCertificateProduct(uuid, ctx)
  }
  // End individual certificate product

  // Subscription offer - mccerts-2548_v1 V1/V2 and every mccerts-2972 arm show
  // subscription prices
  const getSubscriptionOffer = async () => {
    if (!isSubsEntryPointSelectorVariant && !isSubsOnlyClpExp) return null

    // Only the subscription price is read from this offer — the individual cert
    // price comes from `product` (with the cert promo)
    return getCertificateSubscriptionOffer(
      ctx,
      vanityDiscountSlugs,
      certSlug,
      priceTestPlanSlug,
    )
  }
  const getMySubscription = () =>
    userAttributes.is_logged_in && !isPaintedDoor
      ? getMyCertificateSubscription(ctx)
      : Promise.resolve(null)
  // End subscription offer

  return getPageData({
    criticalPromises: {
      program: isPaintedDoor
        ? Promise.resolve(PAINTED_DOOR_PROGRAMS[certSlug])
        : getCertificateProgram(uuid, ctx),
    },
    nonCriticalPromises: {
      product: getProduct(),
      subscriptionOffer: getSubscriptionOffer(), // mccerts-2548_v1, mccerts-2972
      mySubscription: getMySubscription(),
    },
    propsMapper: ({ program: sourceProgram, mySubscription, ...restProps }) => {
      const program = instructorOverrides
        ? { ...sourceProgram, instructors: instructorOverrides }
        : sourceProgram

      // Allow promo copy only when the per-cert promo actually won the
      // best-coupon race — suppresses promo-specific copy when a vanity/cookie
      // code beat the promo.
      const promoCopyAllowed = isPromoCopyAllowed({
        winningDiscountSlug: restProps.product?.winningDiscountSlug,
        promoCoupon,
        productHasDiscount: sendDiscountSlugs,
      })

      return {
        certificates: CERTIFICATES,
        latestCertificates,
        userIsLoggedIn: userAttributes.is_logged_in,
        aapUser: userAttributes.membership === 'AAP',
        certSlug,
        certUuid: uuid,
        promoVariables,
        subscriptionPromoVariables,
        vanityDiscountSlugs,
        whoIsThisFor,
        mccerts2664,
        mccerts3381,
        promoCopyAllowed,
        isPaintedDoor,
        isSubscribed: mySubscription?.isSubscribed ?? false,
        subscriptionPriceTest: priceTestBucket
          ? { planSlug: priceTestPlanSlug ?? null, bucket: priceTestBucket }
          : null,
        isSubsEntryPointSelectorVariant,
        ...restProps,
        program,
      }
    },
  })
}

export default Certificate
